import { Drawer } from "@/components/drawer";
import IWordManagerFormVpc from "@/components/word-manager-form-vpc/word-manager-form-vpc";
import WordManagerFormVpc from "@/components/word-manager-form-vpc/word-manager-form-vpc.vue";
import { CommonLocale, WordManagerFormLocale } from "@/locales/localeid";
import { VCSLevelModel } from "@/models/title-list-model";
import {
	RWUnitGroupsReponseModel,
	WordManagerModel,
	WordManagerSaveModel,
	WordMangerVpcModel
} from "@/models/word-manager-model";
import { MediaUploadState } from "@/modules/media-upload";
import { VersionGroupModule } from "@/modules/versiongroup";
import { WordManagerService } from "@/services/word-manager-service";
import { CurriculumType, MediaSlot } from "@/util/enum";
import { Button, Form, Icon, Input, Select, Tag, Tooltip } from "ant-design-vue";
import { WrappedFormUtils } from "ant-design-vue/types/form/form";
import { fmtMsg, isGuid, MessageHelper, NotificationType, PrimaryLanguageLocale, ServerExceptionCode } from "commonui";
import { cloneDeep, findIndex, isNull, isUndefined } from "lodash";
import { v4 } from "uuid";
import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";

@Component({
	components: {
		Drawer,
		Button,
		AppForm: Form,
		Item: Form.Item,
		Input,
		Icon,
		InputTextArea: Input.TextArea,
		Select,
		Option: Select.Option,
		Tag,
		Tooltip,
		WordManagerFormVpc
	}
})
export default class WordManagerForm extends Vue {
	@Ref() readonly lemmaInputRef!: any;
	@Ref("vpcFormRef") readonly vpcFormRef!: IWordManagerFormVpc;
	@Prop({ default: false }) isDrawerVisible!: boolean;
	@Prop() onClose!: (isRefreshRequired: boolean) => void;
	@Prop({ default: 0 }) wordId!: number;
	@Prop({ default: null }) wordInfo!: WordManagerModel;
	@Prop({ default: [] }) levels!: VCSLevelModel[];

	public isLoading: boolean = false;
	public rwUnitSelected: number = 0;
	public inputVisible: boolean = false;
	public inputValue: string = "";
	public wordManagerForm!: WrappedFormUtils;
	public lemmas: string[] = [];
	public readonly noneUnitId = 0;
	public gsExposureUnit: string = "";
	public lsExposureUnit: string = "";
	public rwUnitSelectedGroups: RWUnitGroupsReponseModel[] = [];
	public rwUnitGroupSelected: number | null = null;
	public fetchRWUnitGroup: boolean = false;
	public versionGroupModule = VersionGroupModule;

	private mediaUploadModule = getModule(MediaUploadState);
	public wordRules = [
		{
			required: true,
			message: this.$format(WordManagerFormLocale.WordManagerFormWordRequiredMessage),
			trigger: "change"
		},
		{
			validator: this.wordValidator,
			trigger: "change"
		}
	];

	created() {
		this.setForm();
	}

	mounted() {
		this.setFormValues();
	}

	@Watch("isDrawerVisible")
	onVisibleChange() {
		if (this.isDrawerVisible) {
			this.setForm();
			this.setFormValues();
		}
	}

	private setForm() {
		this.wordManagerForm = this.$form.createForm(this, { name: "wordManagerForm" });
	}

	private setFormValues() {
		if (!isNull(this.wordInfo)) {
			this.wordManagerForm.getFieldDecorator("word", {
				initialValue: this.wordInfo.word,
				preserve: true
			});
			this.wordManagerForm.getFieldDecorator("pronunciation", {
				initialValue: this.wordInfo.pronunciation,
				preserve: true
			});
			this.wordManagerForm.getFieldDecorator("partofspeech", {
				initialValue: this.wordInfo.partOfSpeech,
				preserve: true
			});
			this.wordManagerForm.getFieldDecorator("description", {
				initialValue: this.wordInfo.definition,
				preserve: true
			});

			if (!isNull(this.wordInfo.rwAssignedUnitId)) {
				this.rwUnitSelected = this.wordInfo.rwAssignedUnitId;
				this.rwUnitGroupSelected = this.wordInfo.rwgroupId;
				WordManagerService.getRWUnitGroups(this.rwUnitSelected).then((data) => {
					this.rwUnitSelectedGroups = data;

					this.wordManagerForm.getFieldDecorator("rwgroup", {
						initialValue: this.wordInfo.rwgroupId,
						preserve: true
					});
				});
			}

			this.gsExposureUnit = this.wordInfo.firstGSUnit;
			this.lsExposureUnit = this.wordInfo.firstLSUnit;
			this.lemmas = this.wordInfo.wordSpecLemma;
		} else {
			this.gsExposureUnit = "";
			this.lsExposureUnit = "";
			this.lemmas = [];
			this.rwUnitSelected = 0;
			this.rwUnitGroupSelected = null;
			this.rwUnitSelectedGroups = [];
		}
	}

	public get vpcLoading(): boolean {
		return this.vpcFormRef && this.vpcFormRef.loading;
	}

	getUnitDisplayName(id: number) {
		const level = this.levels.find((l) => l.id === id);
		if (!isUndefined(level)) {
			return level.displayName;
		} else {
			return "";
		}
	}

	get drawerTitle() {
		return this.wordId === 0
			? this.localizedString(this.wordManagerFormLocale.WordManagerFormAddNewTitle)
			: this.localizedString(this.wordManagerFormLocale.WordManagerFormEditTitle);
	}

	setLoading(value: boolean) {
		this.isLoading = value;
	}

	get wordManagerFormLocale() {
		return WordManagerFormLocale;
	}

	handleInputChange(e) {
		this.inputValue = e.target.value.trim();
	}

	showInput() {
		this.inputVisible = true;
		this.$nextTick(function () {
			this.lemmaInputRef.focus();
		});
	}

	handleClose(removedTag) {
		const lemmas = this.lemmas.filter((tag) => tag !== removedTag);
		this.lemmas = lemmas;
	}

	handleInputConfirm() {
		const inputValue = this.inputValue;
		let lemmas = this.lemmas;

		if (inputValue && findIndex(lemmas, (l) => l.trim().toLowerCase() === inputValue.trim().toLowerCase()) === -1) {
			lemmas = [...lemmas, inputValue];
		}
		Object.assign(this, {
			lemmas,
			inputVisible: false,
			inputValue: ""
		});
	}

	localizedString(id: string, params?: {}) {
		return fmtMsg(id, params);
	}

	getFormParams(values): Promise<WordManagerSaveModel> {
		return new Promise((resolve, reject) => {
			const gsVersionGroupId = isGuid(this.versionGroupModule.selectedGsVersion)
				? this.versionGroupModule.selectedGsVersion
				: null;
			const lsVersionGroupId = isGuid(this.versionGroupModule.selectedLsVersion)
				? this.versionGroupModule.selectedLsVersion
				: null;

			const vpcs = this.vpcFormRef.getVpcs();
			// const vpcMedia = vpcs.map((vpc) => vpc.media); // array of array of media
			// const mediaFilesToUpload = vpcMedia
			// 	.filter((mediaArr) => mediaArr && mediaArr.length)
			// 	.reduce((prev, curr) => curr && prev && prev.concat(curr))
			// 	?.filter((m) => m && m.fileUrl instanceof File);
			const getResult = (): WordManagerSaveModel => {
				const vpcInfo = vpcs.map<WordMangerVpcModel>((vpc) => ({
					id: vpc.id,
					levelId: vpc.levelId!,
					unit: vpc.unit,
					versionGroupId: vpc.versionGroupId!,
					sequence: vpc.sequence!,
					isReview: vpc.isReview!,
					media: vpc.media?.map((vpcMedia) => ({
						isMediaReplaced: vpcMedia.isMediaReplaced,
						isMediaRemoved: vpcMedia.isMediaRemoved,
						fileUrl: vpcMedia.fileUrl as string,
						mediaSlotId: vpcMedia.mediaSlotId,
						mediaId: vpcMedia.mediaId ? vpcMedia.mediaId : v4(),
						isMediaAdded: vpcMedia.isMediaAdded,
						size: vpcMedia.size
					}))
				}));
				return {
					id: this.wordId,
					word: values.word,
					wordSpecLemma: this.lemmas,
					lsExposureUnitId: null,
					gsExposureUnitId: null,
					rwAssignedUnitId: this.rwUnitSelected === this.noneUnitId ? null : this.rwUnitSelected,
					pronunciation: values.pronunciation,
					definition: values.description,
					partOfSpeech: values.partofspeech,
					vpcInfo,
					rwGroupId: this.rwUnitGroupSelected,
					gsVersionGroupId: gsVersionGroupId,
					lsVersionGroupId: lsVersionGroupId
				};
			};

			resolve(getResult());

			// if (mediaFilesToUpload?.length) {
			// 	Promise.all(
			// 		mediaFilesToUpload.map((vpcMedia) => {
			// 			const id = v4();
			// 			return this.mediaUploadModule
			// 				.uploadFile({
			// 					id,
			// 					file: vpcMedia.fileUrl as File,
			// 					slotId:
			// 						this.vpcFormRef.getFileType(vpcMedia.fileUrl) === "pdf"
			// 							? MediaSlot.ViewingContent
			// 							: MediaSlot.Audio,
			// 					languageCode: PrimaryLanguageLocale.English
			// 				})
			// 				.then(() => {
			// 					const uploadedFile = this.mediaUploadModule.fileUploads.find((file) => file.id === id)!;
			// 					vpcMedia.fileUrl = uploadedFile.filePath;
			// 					return vpcMedia;
			// 				});
			// 		})
			// 	)
			// 		.then(() => {
			// 			resolve(getResult());
			// 		})
			// 		.catch(() => {
			// 			reject("File upload error...");
			// 		});
			// } else {
			// 	resolve(getResult());
			// }
		});
	}

	validateAndSubmitForm(e: any) {
		this.wordManagerForm.validateFields((err, values) => {
			if (!err) {
				this.setLoading(true);
				this.getFormParams(values)
					.then((params) => {
						WordManagerService.saveUpdateWordSpec(params)
							.then((data) => {
								if (data === ServerExceptionCode.DulicateWordExeption) {
									MessageHelper.Message(
										NotificationType.Warning,
										this.localizedString(
											this.wordManagerFormLocale.WordManagerFormDuplicateWordException
										)
									);
								} else {
									MessageHelper.Message(
										NotificationType.Success,
										this.localizedString(this.wordManagerFormLocale.WordManagerFormSaveSuccessMsg)
									);
									this.onClose(true);
									setTimeout(() => {
										WordManagerService.calculateWordCount({ Word: params.word });
									}, 3000);
								}
								this.setLoading(false);
							})
							.catch((err) => {
								if (err?.body?.error_code) {
									this.showErrorMessage({ error_code: err.body.error_code });
								} else if (err?.error_code) {
									this.showErrorMessage({ error_code: err.error_code });
								}
								this.setLoading(false);
							});
					})
					.catch(() => {
						this.setLoading(false);
					});
			}
		});
	}

	private showErrorMessage({ error_code }) {
		switch (error_code) {
			case ServerExceptionCode.WordManagerVPCMediaReferenceExists:
				MessageHelper.Message(
					NotificationType.Failed,
					this.$format(this.wordManagerFormLocale.WordManagerFormVPCReferenceException)
				);
				break;
			case ServerExceptionCode.NoSpecialDesignationException:
				MessageHelper.Message(
					NotificationType.Failed,
					this.$format(CommonLocale.CommonSpecialDesignationNotExist)
				);
				break;
		}
	}

	@Watch("rwUnitSelected")
	rwUnitChanged() {
		if (
			(this.rwUnitSelected !== this.noneUnitId && this.fetchRWUnitGroup) ||
			isNull(this.wordInfo) ||
			(!isNull(this.wordInfo) && this.wordInfo.rwAssignedUnitId === this.noneUnitId)
		) {
			WordManagerService.getRWUnitGroups(this.rwUnitSelected).then((data) => {
				if (data.length) {
					this.rwUnitSelectedGroups = data;
					this.rwUnitGroupSelected = data[0].groupId;
					this.rwUnitGroupSelectedChange(data[0].groupId);
				} else {
					this.rwUnitSelectedGroups = [];
					this.rwUnitGroupSelected = null;
					this.rwUnitGroupSelectedChange(null);
				}
			});
		} else if (this.rwUnitSelected === this.noneUnitId) {
			this.rwUnitGroupSelected = null;
			this.rwUnitGroupSelectedChange(null);
		}
		this.fetchRWUnitGroup = true;
	}

	get isWordAssociatedWithUnit() {
		if (this.wordInfo) {
			return this.wordInfo.firstLSUnit || this.wordInfo.firstGSUnit ? false : true;
		} else {
			return false;
		}
	}

	removeWord() {
		MessageHelper.Confirm(
			this.localizedString(WordManagerFormLocale.WordManagerFormWarningWordDelete),
			(): Promise<void> =>
				new Promise((resolve, reject) => {
					{
						WordManagerService.deleteWord(this.wordId)
							.then((data) => {
								if (data) {
									this.onClose(true);
								}
							})
							.catch((ex) => {
								if (ex?.body?.error_code == ServerExceptionCode.WordAssociatedWithRelateItemException) {
									MessageHelper.Message(
										NotificationType.Failed,
										this.$format(this.wordManagerFormLocale.WordManagerFormHasAssociateRelateItem)
									);
								} else {
									MessageHelper.Message(NotificationType.Warning, ex);
								}
							});
						resolve();
					}
				})
		);
	}

	findVersionId(levelId) {
		const level = this.levels.find((level) => level.id === levelId);
		const versionId =
			level?.curriculumType === (CurriculumType.GrapeSEED as number)
				? VersionGroupModule.selectedGsVersion
				: VersionGroupModule.selectedLsVersion;
		return versionId;
	}

	rwUnitGroupSelectedChange(value) {
		this.wordManagerForm.setFieldsValue({ rwgroup: value });
		this.rwUnitGroupSelected = value;
	}

	wordValidator(rule: any, value: string, callback: (msg?: string) => void) {
		const val = value?.trim().toLowerCase() || "";
		const origVal = this.wordInfo?.word?.trim().toLowerCase() || "";

		if (val && origVal) {
			if (origVal === val) {
				callback();
			} else {
				callback(this.$format(WordManagerFormLocale.WordManagerNoWordChangeMsg));
			}
		} else {
			callback();
		}
	}

	get isBothVersionNone() {
		return VersionGroupModule.selectedGsVersion === "-1" && VersionGroupModule.selectedLsVersion === "-1";
	}
}
